import React, { useState } from "react";
import { colors2, LoaderOverlay, UI_CAPTION_SEMIBOLD } from "oolib";
import { ButtonPrimary, ButtonTertiary } from "oolib/dist/v2/components/Buttons";
import { TextInput } from "oolib/dist/v2/components/TextInputs";
import { DropdownSingle } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import {
  extractAppStoreId,
  REVIEW_COUNT_OPTIONS,
} from "../../DirectDataImport/comps/IntegrationInterfaces/AppStoreReviewsInterface";
import { IntegrationModalShell } from "../comps/IntegrationModalShell";
import { ManageDatasetsModal } from "../comps/ManageDatasetsModal";
import { caption } from "../comps/common";
import { useExternalConnectionStatus } from "@/utils/react-query-hooks/externalConnections";

/**
 * App Store Reviews' popups, on IntegrationModalShell. Nothing to connect and
 * nothing to sync, so no connection card or schedule.
 *
 * ImportModal is the import wizard's App Store step: Continue hands the
 * adapter options to onContinue; the wizard passes back isLoading / error.
 *
 * ManageModal is the shared ManageDatasetsModal: its datasets, delete one or all.
 */

function ImportModal({
  Icon,
  onClose,
  onBack,
  onContinue,
  isLoading,
  error,
  hideBackdrop,
}) {
  // "Do Later" only before App Store has any dataset; after that, "Discard".
  const { data: conn } = useExternalConnectionStatus("appStoreReviews");
  const hasDatasets = conn?.links?.length > 0;
  const [appInput, setAppInput] = useState("");
  const [count, setCount] = useState(REVIEW_COUNT_OPTIONS[1]); // 100
  const appId = extractAppStoreId(appInput);
  return (
    <IntegrationModalShell
      title="Import from App Store"
      Icon={Icon}
      onBack={onBack}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      footer={
        <>
          <ButtonTertiary onClick={onClose}>
            {hasDatasets ? "Discard" : "Do Later"}
          </ButtonTertiary>
          <ButtonPrimary
            onClick={() => onContinue({ appId, maxRecords: count.value })}
            disabled={!appId || isLoading}
            isLoading={isLoading}
          >
            Continue
          </ButtonPrimary>
        </>
      }
    >
      {isLoading && (
        <LoaderOverlay loaderText="Fetching data from external source..." />
      )}
      {caption(
        "Import App Store reviews into your dataset. Each review becomes a row, with its rating, author and any developer reply.",
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <TextInput
          id="appId"
          label="App Store link or ID"
          placeholder="e.g. https://apps.apple.com/app/spotify-music/id324684580"
          value={appInput}
          onChange={(_k, v) => setAppInput(v)}
          readOnly={isLoading}
        />
        {appInput.trim() && !appId && (
          <UI_CAPTION_SEMIBOLD style={{ color: colors2.red }}>
            Could not find an App Store ID. Paste the App Store link, or the
            numeric ID itself.
          </UI_CAPTION_SEMIBOLD>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <DropdownSingle
          id="reviewCount"
          label="Maximum reviews to fetch"
          placeholder="Select a limit..."
          options={REVIEW_COUNT_OPTIONS}
          value={count}
          onChange={(_k, v) => setCount(v)}
          readOnly={isLoading}
        />
        {caption("Larger imports take longer to complete.")}
      </div>
      {error && (
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.red }}>
          {error}
        </UI_CAPTION_SEMIBOLD>
      )}
    </IntegrationModalShell>
  );
}

// The shared dataset-only manage popup.
const ManageModal = (props) => (
  <ManageDatasetsModal provider="appStoreReviews" {...props} />
);

export const appStore = {
  ImportModal,
  ManageModal,
};
